<script setup lang="ts">
import { useRoute } from '#imports';
import { getApiRoot } from '~/utilities/api';
import { formatXchPrice } from '~/utilities/chia-display-utils';
import { BASE_TITLE } from '~/utilities/constants';

const route = useRoute();

const { data: offer } = await useFetch<any>(`${getApiRoot()}/offers/${route.params.id}`);
const { data: nft } = await useFetch<any>(`${getApiRoot()}/nfts/${offer.value?.nft_id}`);

const title = computed(() => {
  if (offer.value && nft.value) {
    const offeredThings = Object.keys(offer.value.offered || {});
    const requestedThings = Object.keys(offer.value.requested || {});

    const isNftNftOffer =
      !offer.value.xch_price &&
      offeredThings.length === 1 &&
      offeredThings[0].startsWith('nft') &&
      requestedThings.length === 1 &&
      requestedThings[0].startsWith('nft');

    const name = nft.value?.data.metadata_json?.name;
    const price = formatXchPrice(offer.value?.xch_price);
    if (offer.value.side === 1) {
      return `Offering "${name}" for ${price}`;
    } else if (isNftNftOffer) {
      return `Offering "${name}"`;
    } else {
      return `Offering ${price} for "${name}"`;
    }
  } else if (offer.value) {
    return offer.value.id;
  } else {
    return 'Unknown offer';
  }
});
useHead({
  title: `${title.value} ${BASE_TITLE}`,
  meta: [
    { property: 'og:title', content: `${title.value} ${BASE_TITLE}` },
    {
      property: 'og:image',
      content: nft.value?.data?.thumbnail_uri || nft.value?.thumbnail_uri,
    },
    { name: 'twitter:image', content: nft.value?.data?.thumbnail_uri || nft.value?.thumbnail_uri },
    { name: 'twitter:title', content: `${title.value} ${BASE_TITLE}` },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
} as any);
</script>

<template>
  <Container>
    <Error404 v-if="!offer || !nft" />
    <div v-else class="">
      <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div class="relative w-full max-w-md flex-grow rounded-lg pb-4 pt-5 text-left sm:my-8 lg:w-full lg:max-w-4xl">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 flex-grow text-center sm:mt-0">
              <h3 class="mt-2 text-3xl font-extrabold tracking-tight text-neutral-900 dark:text-neutral-50 sm:text-4xl">
                {{ $t('offers.offer') }}
              </h3>
              <span
                class="cursor-pointer font-mono text-xs tracking-tight text-neutral-500 dark:text-neutral-400 lg:text-base"
                >{{ offer.id }}</span
              >
              <div class="mt-4">
                <span
                  v-if="offer.status === 0"
                  class="inline-flex items-center rounded-md bg-green-50 px-3 py-1 font-semibold text-green-700 ring-1 ring-inset ring-green-600/20"
                  >ACTIVE</span
                >
                <span
                  v-if="offer.status === 1"
                  class="inline-flex animate-pulse items-center rounded-md bg-purple-50 px-3 py-1 font-semibold text-purple-700 ring-1 ring-inset ring-purple-600/20"
                  >PENDING</span
                >
                <span
                  v-if="offer.status === 3"
                  class="inline-flex items-center rounded-md bg-neutral-50 px-3 py-1 font-semibold text-neutral-700 ring-1 ring-inset ring-neutral-600/20 dark:text-neutral-200"
                  >CANCELED</span
                >
                <span
                  v-if="offer.status === 4"
                  class="inline-flex items-center rounded-md bg-blue-50 px-3 py-1 font-semibold text-blue-700 ring-1 ring-inset ring-blue-600/20"
                  >CONFIRMED</span
                >
                <span
                  v-if="offer.status === 6"
                  class="inline-flex items-center rounded-md bg-neutral-50 px-3 py-1 font-semibold text-neutral-700 ring-1 ring-inset ring-neutral-600/20 dark:text-neutral-200"
                  >EXPIRED</span
                >
              </div>
              <OfferDetails :offer="offer" :nft="nft" />
              <OfferButtons v-if="offer.status < 3" :offer="offer" :nft="nft" />
              <div v-else class="grid grid-cols-1 gap-4 lg:grid-cols-2"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Container>
</template>
